import { apiFetch } from '@/lib/api';
import { useOrgStore } from '@/stores/orgStore';
import type { PlacePhoto, PlacePhotosResponse } from '@/services/PlacesService';

export interface VenuePhoto {
  id: string;
  venue_id: string;
  organization_id: string;
  attachment_id: string | null;
  url: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ResolvedPhotosResponse {
  venue_id: string;
  place_id?: string;
  photos: (PlacePhoto & { source?: 'custom' | 'google' })[];
  source: 'custom' | 'google' | 'none';
}

export class VenuePhotoService {
  private orgHeaders(): Record<string, string> {
    try {
      const stateOrgId = useOrgStore.getState().activeOrgId;
      const storedOrgId = localStorage.getItem('ima_active_org_id');
      const orgId = stateOrgId || storedOrgId || undefined;
      return orgId ? { 'X-Organization-Id': orgId } : {};
    } catch {
      return {};
    }
  }

  /** List custom photos for a venue */
  async listPhotos(venueId: string): Promise<VenuePhoto[]> {
    const res = await apiFetch(`/api/venues/${encodeURIComponent(venueId)}/photos`, {
      headers: this.orgHeaders(),
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.data || [];
  }

  /** Add a custom photo to a venue (after uploading via AttachmentsService) */
  async addPhoto(venueId: string, attachmentId: string): Promise<VenuePhoto> {
    const res = await apiFetch(`/api/venues/${encodeURIComponent(venueId)}/photos`, {
      method: 'POST',
      headers: { ...this.orgHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ attachment_id: attachmentId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: { message: 'Failed to add photo' } }));
      throw new Error(err.error?.message || 'Failed to add photo');
    }
    const data = await res.json();
    return data.data;
  }

  /** Add a custom photo by external image URL (stored directly, no upload) */
  async addPhotoFromUrl(venueId: string, url: string): Promise<VenuePhoto> {
    const res = await apiFetch(`/api/venues/${encodeURIComponent(venueId)}/photos/from-url`, {
      method: 'POST',
      headers: { ...this.orgHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: { message: 'Failed to add photo from URL' } }));
      throw new Error(err.error?.message || 'Failed to add photo from URL');
    }
    const data = await res.json();
    return data.data;
  }

  /** Remove a custom photo */
  async removePhoto(venueId: string, photoId: string): Promise<void> {
    const res = await apiFetch(
      `/api/venues/${encodeURIComponent(venueId)}/photos/${encodeURIComponent(photoId)}`,
      { method: 'DELETE', headers: this.orgHeaders() },
    );
    if (!res.ok) {
      throw new Error('Failed to remove photo');
    }
  }

  /** Reorder custom photos */
  async reorderPhotos(venueId: string, photoIds: string[]): Promise<void> {
    const res = await apiFetch(`/api/venues/${encodeURIComponent(venueId)}/photos/reorder`, {
      method: 'PUT',
      headers: { ...this.orgHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ photoIds }),
    });
    if (!res.ok) {
      throw new Error('Failed to reorder photos');
    }
  }

  /**
   * Get resolved photos for a venue (custom if exist, else Google).
   * Works for both authenticated users and share link sessions.
   */
  async getResolvedPhotos(
    venueId: string,
    maxWidth: number = 800,
    shareLinkToken?: string,
    sessionId?: string,
  ): Promise<ResolvedPhotosResponse> {
    let url = `/api/venues/${encodeURIComponent(venueId)}/resolved-photos?maxwidth=${maxWidth}`;
    if (shareLinkToken) {
      url += `&share_link_token=${encodeURIComponent(shareLinkToken)}`;
    }

    const headers: Record<string, string> = { ...this.orgHeaders() };
    if (sessionId) {
      headers['X-Session-Id'] = sessionId;
    }

    const res = await apiFetch(url, {
      headers,
      skipRedirect: true,
      skipRefresh: true,
      credentials: 'include',
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Failed to fetch venue photos' }));
      throw new Error(err.message || 'Failed to fetch venue photos');
    }

    const data = await res.json();
    return data.data;
  }
}
